import { Chip, Stack } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

export type QuickReply = {
  label: string;
  icon?: string;
  /** The quieter, neutral chip: the less likely answer. */
  secondary?: boolean;
  /**
   * The way on rather than an answer: for a question that stays open for
   * several messages, it says they are done.
   */
  go?: boolean;
};

/**
 * Answers offered as chips just above the text field, side by side, so the
 * likely replies sit where the typing does. Tapping one sends it, the same as
 * typing it.
 *
 * Soft fills rather than outlines: the main answer in the light teal the
 * chat's answer rows use, the other a quieter neutral. Charcoal text on both,
 * 7:1 or better. One line, scrolling sideways if they do not fit, so the bar
 * never grows taller for them.
 */
const QuickReplies = ({
  replies,
  onPick,
}: {
  replies: QuickReply[];
  onPick: (reply: QuickReply) => void;
}) => (
  <Stack
    direction='row'
    spacing={1}
    sx={{ overflowX: 'auto', flexWrap: 'nowrap', pb: 0.25 }}
  >
    {replies.map((reply) => (
      <Chip
        key={reply.label}
        label={reply.label}
        variant='filled'
        icon={
          reply.icon ? (
            <Icon icon={reply.icon as never} fontSize={12} />
          ) : undefined
        }
        onClick={() => onPick(reply)}
        sx={{
          flexShrink: 0,
          bgcolor: reply.secondary
            ? 'custom.neutral.200'
            : 'custom.marineGreen.50',
          color: 'text.primary',
          '& .MuiChip-icon': { color: 'text.primary' },
          '&:hover': {
            bgcolor: reply.secondary
              ? 'custom.neutral.300'
              : 'custom.marineGreen.50',
          },
        }}
      />
    ))}
  </Stack>
);

QuickReplies.displayName = 'QuickReplies';
export default QuickReplies;
